import { useContext, useMemo, useEffect, useState, useCallback, useRef, type ReactNode } from 'react'
import { createRealEngineManager } from '../engine/realWorkspace'
import {
    parseWorkspaceFromHash,
    saveGuestCapToSession,
    getGuestCapFromSession,
    getActiveWorkspace,
    savePendingWorkspace,
    getPendingWorkspace,
    clearPendingWorkspace,
} from '../engine/parseUrl'
import { deriveHomeWorkspace } from '../engine/deriveHome'
import { createWelcomeWorkspace } from '../engine/welcomeWorkspace'
import { installTestMode } from '../engine/testMode'
import { installRuntimeEventTrace } from '../testing/runtimeEventTrace'
import { API_BASE_URL } from '../config'
import { SkeletonFullPage } from '../components/Skeleton'
import {
    Workspace, FileStat, SyncState, CommitSummary, FsChangeEvent, FsExternalChangeEvent,
    EngineManager, WorkspaceEntry, UserIdentity, TrustedDeviceInfo, WorkspaceSyncPolicy,
} from '../engine/types'
import { RealWorkspaceSession, redeemWorkspaceInviteV2 } from '@secureblob/sdk'
import { SecureBlobProvider } from '@secureblob/react'

import { WorkspaceContext, EngineManagerContext } from './workspaceContext'
import { generateWorkspaceSecretV2 } from '../engine/mcp/crypto'
import { DEFAULT_AGENT_CONFIG_JSON } from '../engine/defaultAgentConfig'

/** Rich onboarding content for a user's auto-created home workspace */
const HOME_WORKSPACE_CONTENT = (name: string) => `# Welcome, ${name}! 👋

This is your personal workspace — **end-to-end encrypted** and synced across devices.

---

## ✨ Getting started

This workspace is your private scratch pad. Everything you write here is encrypted with
a key only you control. Not even the server can read your content.

**Try it out:** Edit this file, create new ones, or drag-and-drop images.

---

## 🤖 Built-in AI assistant

This workspace includes **assistant.agent.json** — click it in the file browser to start chatting.
Just add your API key and optional API base URL and you're ready to go.

The assistant can **read and write files** in your workspace, all end-to-end encrypted.

### 🔌 MCP connections

You can also give external AI tools (Cursor, Windsurf, Claude Desktop, etc.) direct access:

1. Click your **profile avatar** (top-right)
2. Go to the **MCP** tab → **New MCP Connection**
3. Copy the config snippet into your AI assistant's settings

---

## 🔗 Share a workspace

Every workspace has a **shareable link** with its encryption key embedded.
Click the **Share** button in the toolbar to generate a link you can send to collaborators.

Anyone with the link can decrypt and collaborate — no account required.

---

## 📐 Rich content support

SecureBlob renders **Markdown**, **MathML**, and **Mermaid diagrams** in preview mode:

$$
E = mc^2
$$

\`\`\`mermaid
graph LR
    A[Your Device] -->|Encrypted| B[SecureBlob Server]
    B -->|Encrypted| C[Collaborator]
    B -->|MCP| D[AI Agent]
\`\`\`

---

## ⌨️ Keyboard shortcuts

| Shortcut | Action |
|---|---|
| \`⌘ K\` | Open workspace palette |
| \`⌘ S\` | Save & sync |
| \`⌘ P\` | Toggle preview |

---

*Delete this file anytime — it's just regular content in your workspace.*
`

// parseWorkspaceFromHash is now imported from '../engine/parseUrl'

// --- Provider ---

export function WorkspaceProvider({ children }: { children: ReactNode }) {
    const manager = useMemo(() => createRealEngineManager(), [])
    const [workspace, setWorkspace] = useState<Workspace | null>(null)
    const [bootstrapError, setBootstrapError] = useState<string | null>(null)
    const [retryNonce, setRetryNonce] = useState(0)
    const [loadingSeconds, setLoadingSeconds] = useState(0)
    const isWelcomeRef = useRef(false)

    useEffect(() => {
        installRuntimeEventTrace(manager)
        installTestMode(manager)
    }, [manager])

    useEffect(() => {
        let cancelled = false
        const upsertBootstrapEntry = (entry: WorkspaceEntry) => {
            const existing = manager.getRegistry().find(e => e.blobId === entry.blobId)
            if (!existing) {
                manager.addToRegistry(entry)
                return
            }
            manager.addToRegistry({
                ...existing,
                ...entry,
                // Preserve user-local naming while still upgrading capability/access.
                label: existing.label,
                emoji: existing.emoji,
                canWrite: entry.canWrite ?? existing.canWrite,
                trustLevel: entry.trustLevel ?? existing.trustLevel,
                agentSecretsAllowed: entry.agentSecretsAllowed ?? existing.agentSecretsAllowed,
            })
        }

        const parsed = parseWorkspaceFromHash(window.location.hash)
        if (parsed.error) {
            setBootstrapError(parsed.error)
            return () => {
                cancelled = true
                // NOTE: do NOT call manager.dispose() here — manager is stable (useMemo)
                // and retryNonce changes re-run this effect with the same instance.
            }
        }

        // Force demo mode for debug/testing
        if (window.location.hash === '#demo') {
            isWelcomeRef.current = true
            setWorkspace(createWelcomeWorkspace())
            return () => {
                cancelled = true
            }
        }

        const hasHash = !!parsed.entry || !!parsed.identityPayload

        // Guest-cap fallback: if no hash but sessionStorage has a guest session,
        // recover it so F5 in ReadWorkspace doesn't lose the workspace.
        const guestFallback = !hasHash ? getGuestCapFromSession(sessionStorage) : null
        const effectiveEntry = parsed.inviteV2 ? null : (parsed.entry ?? guestFallback)

        // Register hash-based entry eagerly. Existing entries may have
        // user-customized labels, so preserve local metadata while still
        // upgrading capability/access when a stronger link for the same blob arrives.
        if (parsed.entry && !parsed.inviteV2) {
            upsertBootstrapEntry(parsed.entry)
            // Cache guest cap for F5 resilience
            saveGuestCapToSession(sessionStorage, parsed.entry)
            // Save as pending workspace for guest→profile transition (§8)
            savePendingWorkspace(sessionStorage, parsed.entry)
            // §1.3.1.1: Clear hash eagerly for writable links (security: no capability leak).
            // Read-only guest links keep the hash (persistent shareable URL).
            // Done BEFORE activation so the capability is removed from the URL bar
            // even if workspace activation fails (cap is already in sessionStorage).
            if (parsed.entry.canWrite && window.location.hash) {
                history.replaceState(null, '', location.pathname + location.search)
            }
        } else if (guestFallback) {
            upsertBootstrapEntry(guestFallback)
        }

        const unsubscribe = manager.on('workspace:switched', ws => {
            if (cancelled) return
            if (ws) {
                setWorkspace(ws)
            } else {
                // Workspace was left or deleted — fall back to welcome state
                // so AppShell stays mounted (provider hides children when
                // workspace is null). App-level logic (LeaveWorkspaceModal's
                // onLeft callback) can still show the workspace picker etc.
                isWelcomeRef.current = true
                setWorkspace(createWelcomeWorkspace())
            }
        })

        // Helper: ensure deterministic Home workspace derived from primarySeed.
        // All devices with the same identity share the same Home blobId.
        async function ensureHomeWorkspace(): Promise<void> {
            const identity = manager.getIdentity()
            const canonical = identity?.canonical
            if (!canonical?.primarySeed) {
                console.warn('ensureHomeWorkspace: no canonical identity available')
                return
            }

            const { blobId, workspaceSecret } = deriveHomeWorkspace(canonical.primarySeed)

            // The Home workspace is deterministic and immortal — if it was
            // previously tombstoned (e.g. user deleted it), clear the tombstone
            // so it can be re-created. Home should always be available.
            if (manager.isTombstoned(blobId)) {
                console.info('[WorkspaceProvider] Home workspace was tombstoned, clearing tombstone to re-create')
                manager.removeTombstone(blobId)
            }

            const userName = identity?.name || 'there'

            const entry: WorkspaceEntry = {
                blobId,
                label: 'Home',
                emoji: '🏠',
                apiBaseUrl: API_BASE_URL,
                capability: workspaceSecret,
                canWrite: true,
                trustLevel: 'trusted',
                agentSecretsAllowed: true,
            }
            manager.addToRegistry(entry)
            const ws = await manager.activateWorkspace(blobId)
            if (!cancelled) {
                isWelcomeRef.current = false
                setWorkspace(ws)
                // Scaffold only on first-ever creation (§3.5)
                if (ws.fs.list().length === 0) {
                    ws.fs.writeText('/welcome.md', HOME_WORKSPACE_CONTENT(userName))
                    ws.fs.writeText('/assistant.agent.json', DEFAULT_AGENT_CONFIG_JSON)
                }
            }
        }

        // When user creates/imports identity while in welcome workspace, transition out.
        // When identity is lost (logout/auto-lock/delete), transition back to welcome.
        const unsubIdentity = manager.on('identity:changed', () => {
            if (cancelled) return

            if (!manager.hasIdentity()) {
                // Identity was lost (logout, auto-lock, or delete) → go to welcome
                isWelcomeRef.current = true
                setWorkspace(createWelcomeWorkspace())
                return
            }

            // Bug 4 fix: In ReadWorkspace (guest) mode, re-activate workspace
            // with identity so user gets write permissions after login.
            if (!isWelcomeRef.current) {
                const pending = getPendingWorkspace(sessionStorage)
                clearPendingWorkspace(sessionStorage)
                if (pending) {
                    void (async () => {
                        try {
                            upsertBootstrapEntry(pending)
                            const ws = await manager.activateWorkspace(pending.blobId)
                            if (!cancelled) setWorkspace(ws)
                        } catch (e) {
                            console.warn('Re-activate after login failed:', e)
                        }
                    })()
                }
                return
            }

            void (async () => {
                try {
                    // §4.1 step 6: Restore last-active workspace from sessionStorage first
                    // Each activation wrapped in try/catch for auto-recovery (same pattern as bootstrap)
                    const savedBlobId = getActiveWorkspace(sessionStorage)
                    const savedEntry = savedBlobId
                        ? manager.getRegistry().find(e => e.blobId === savedBlobId)
                        : null

                    let recovered = false

                    if (savedEntry) {
                        try {
                            const ws = await manager.activateWorkspace(savedEntry.blobId)
                            if (!cancelled) {
                                isWelcomeRef.current = false
                                setWorkspace(ws)
                            }
                            recovered = true
                        } catch (e) {
                            console.warn(`[identity:changed] Saved workspace ${savedEntry.blobId} failed, falling back:`, e)
                            sessionStorage.removeItem('sb:active-workspace')
                        }
                    }

                    if (!recovered) {
                        // Fallback: check for pending workspace from hash/guest session (§8)
                        const pendingEntry = effectiveEntry ?? getPendingWorkspace(sessionStorage)
                        if (pendingEntry) {
                            upsertBootstrapEntry(pendingEntry)
                            clearPendingWorkspace(sessionStorage)
                            try {
                                const real = await manager.activateWorkspace(pendingEntry.blobId)
                                if (!cancelled) {
                                    isWelcomeRef.current = false
                                    setWorkspace(real)
                                }
                                recovered = true
                            } catch (e) {
                                console.warn(`[identity:changed] Pending workspace ${pendingEntry.blobId} failed:`, e)
                            }
                        }
                    }

                    if (!recovered && manager.hasIdentity()) {
                        // Step 7: Fallback → registry lastOpenedAt → Home
                        const registry = manager.getRegistry()
                        const byLastOpened = registry
                            .filter(e => (e as any).lastOpenedAt)
                            .sort((a, b) => ((b as any).lastOpenedAt as string).localeCompare((a as any).lastOpenedAt as string))
                        const mruEntry = byLastOpened[0]

                        if (mruEntry) {
                            try {
                                const ws = await manager.activateWorkspace(mruEntry.blobId)
                                if (!cancelled) {
                                    isWelcomeRef.current = false
                                    setWorkspace(ws)
                                }
                                recovered = true
                            } catch (e) {
                                console.warn(`[identity:changed] MRU workspace ${mruEntry.blobId} failed, falling back to Home:`, e)
                            }
                        }

                        if (!recovered) {
                            await ensureHomeWorkspace()
                        }
                    }
                } catch (e) {
                    console.warn('Failed to create first workspace:', e)
                }
            })()
        })

        void (async () => {
            try {
                let effectiveEntryForOpen = effectiveEntry

                if (parsed.inviteV2) {
                    if (!parsed.entry) {
                        if (!cancelled) setBootstrapError('Invalid link: invite is missing workspace secret.')
                        return
                    }

                    try {
                        const redeemResult = await redeemWorkspaceInviteV2(parsed.inviteV2)
                        const redeemedEntry: WorkspaceEntry = {
                            ...parsed.entry,
                            blobId: redeemResult.blobId,
                        }
                        effectiveEntryForOpen = redeemedEntry

                        upsertBootstrapEntry(redeemedEntry)
                        saveGuestCapToSession(sessionStorage, redeemedEntry)
                        savePendingWorkspace(sessionStorage, redeemedEntry)
                    } catch (e) {
                        if (!cancelled) {
                            setBootstrapError(`Invite redeem failed: ${e instanceof Error ? e.message : 'Unknown error'}`)
                        }
                        return
                    }

                    // Invite URLs carry both encryption material and subject seeds.
                    // Once the secret is cached locally, keep the address bar clean.
                    if (window.location.hash) {
                        history.replaceState(null, '', location.pathname + location.search)
                    }
                }

                // Auto-import identity from #identity=... URL if present
                if (parsed.identityPayload) {
                    try {
                        await manager.importIdentity(parsed.identityPayload)
                        // Clear hash to avoid re-importing on refresh
                        history.replaceState(null, '', location.pathname + location.search)
                        // Notify UI (toast) — dispatched as DOM event since toast context is a child
                        window.dispatchEvent(new CustomEvent('sb:identity-imported'))
                    } catch (e) {
                        console.error('Identity auto-import failed:', e)
                        if (!cancelled) {
                            setBootstrapError(`Identity import failed: ${e instanceof Error ? e.message : 'Unknown error'}`)
                        }
                        return
                    }
                } else {
                    // Try silent auto-unlock (works for PIN vaults with cached PIN).
                    // For WebAuthn vaults this returns false immediately — WebAuthn requires
                    // a user gesture (click/tap) to complete, so we can't call it from useEffect.
                    await manager.tryAutoUnlock().catch(e => console.warn('Auto-unlock skipped:', e))
                }

                // If no identity yet → show welcome workspace.
                // The profile button's handleOpenProfile calls unlockWithWebAuthn() on click
                // (providing the required user gesture), then identity:changed creates
                // a first workspace for the user.
                if (!hasHash && !guestFallback && !manager.hasIdentity()) {
                    if (!cancelled) {
                        isWelcomeRef.current = true
                        setWorkspace(createWelcomeWorkspace())
                        setBootstrapError(null)
                    }
                    return
                }

                // Authenticated but no hash/guest workspace → try sessionStorage or Home
                // Each activation is wrapped in try/catch for auto-recovery: if a saved
                // workspace is corrupted/gone, silently fall through to the next option.
                if (!effectiveEntryForOpen) {
                    if (manager.hasIdentity()) {
                        // Spec §4.1 step 6: Try sessionStorage first (F5 reload)
                        const savedBlobId = getActiveWorkspace(sessionStorage)
                        const savedEntry = savedBlobId
                            ? manager.getRegistry().find(e => e.blobId === savedBlobId)
                            : null

                        let recovered = false
                        if (savedEntry) {
                            try {
                                const ws = await manager.activateWorkspace(savedEntry.blobId)
                                if (!cancelled) {
                                    setWorkspace(ws)
                                    setBootstrapError(null)
                                }
                                recovered = true
                            } catch (e) {
                                console.warn(`Saved workspace ${savedEntry.blobId} failed, falling back:`, e)
                                sessionStorage.removeItem('sb:active-workspace')
                            }
                        }

                        if (!recovered) {
                            // Step 7: Fallback → registry lastOpenedAt → Home
                            const registry = manager.getRegistry()
                            const byLastOpened = registry
                                .filter(e => (e as any).lastOpenedAt)
                                .sort((a, b) => ((b as any).lastOpenedAt as string).localeCompare((a as any).lastOpenedAt as string))
                            const mruEntry = byLastOpened[0]

                            if (mruEntry) {
                                try {
                                    const ws = await manager.activateWorkspace(mruEntry.blobId)
                                    if (!cancelled) {
                                        setWorkspace(ws)
                                        setBootstrapError(null)
                                    }
                                    recovered = true
                                } catch (e) {
                                    console.warn(`MRU workspace ${mruEntry.blobId} failed, falling back to Home:`, e)
                                }
                            }
                        }

                        if (!recovered) {
                            await ensureHomeWorkspace()
                        }
                    } else {
                        if (!cancelled) {
                            isWelcomeRef.current = true
                            setWorkspace(createWelcomeWorkspace())
                            setBootstrapError(null)
                        }
                    }
                    return
                }

                const initial = await manager.openWorkspace({
                    apiBaseUrl: effectiveEntryForOpen.apiBaseUrl,
                    blobId: effectiveEntryForOpen.blobId,
                    capability: effectiveEntryForOpen.capability,
                    canWrite: effectiveEntryForOpen.canWrite,
                })
                if (!cancelled) {
                    // Bridge initialPath + initialViewMode to AppShell via sessionStorage.
                    // MUST be set BEFORE setWorkspace() — React may render AppShell
                    // synchronously on state update, and AppShell reads these on mount.
                    if (parsed.initialPath) {
                        sessionStorage.setItem('sb:initial-path', parsed.initialPath)
                    }
                    if (parsed.initialViewMode) {
                        sessionStorage.setItem('sb:initial-view-mode', parsed.initialViewMode)
                    }
                    // Hash clearing for writable links is done eagerly above (before activation).
                    // Read-only guest links keep the hash (§1.3.1.1).
                    // Now trigger the re-render — AppShell will find the
                    // sessionStorage values already in place.
                    setWorkspace(initial)
                    setBootstrapError(null)
                }
            } catch (error) {
                // Only reaches here for URL-based workspace errors (hash/guest cap) —
                // sessionStorage fallbacks are handled above with auto-recovery
                if (!cancelled) {
                    const offline = typeof navigator !== 'undefined' && !navigator.onLine
                    setBootstrapError(
                        offline
                            ? 'Offline – blob ej tillgänglig lokalt.'
                            : `Kan inte dekryptera (fel key) eller läsa workspace. ${error instanceof Error ? error.message : ''}`,
                    )
                }
            }
        })()

        const onHashChange = () => {
            if (!cancelled) setRetryNonce(v => v + 1)
        }
        window.addEventListener('hashchange', onHashChange)

        return () => {
            cancelled = true
            unsubscribe()
            unsubIdentity()
            window.removeEventListener('hashchange', onHashChange)
            // NOTE: do NOT call manager.dispose() here — manager is stable (useMemo)
            // and retryNonce changes re-run this effect with the same instance.
            // Dispose is handled separately on true unmount below.
        }
    }, [manager, retryNonce])

    // Dispose manager only on true component unmount
    useEffect(() => () => manager.dispose(), [manager])


    useEffect(() => {
        if (workspace) {
            setLoadingSeconds(0)
            return
        }
        const started = Date.now()
        const timer = setInterval(() => setLoadingSeconds(Math.floor((Date.now() - started) / 1000)), 250)
        return () => clearInterval(timer)
    }, [workspace, retryNonce])

    // Bridge webapp's Workspace into @secureblob/react's context so all SDK hooks
    // (useSqlite, useFile, useSyncState, etc.) work throughout the app.
    // Must be declared here (before early returns) to satisfy Rules of Hooks.
    const session = useMemo(
        () => workspace ? new RealWorkspaceSession(workspace) : null,
        [workspace],
    )
    useEffect(() => () => {
        if (session) try { session.close() } catch { /* cleanup */ }
    }, [session])

    if (bootstrapError) {
        return (
            <div className="workspace-bootstrap-state">
                <h1>Ogiltig länk / Initieringsfel</h1>
                <p>{bootstrapError}</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <button className="btn" onClick={() => { setWorkspace(null); setRetryNonce(v => v + 1) }}>Försök igen</button>
                    <button className="btn" style={{ opacity: 0.8 }} onClick={() => {
                        // Clear corrupted workspace from sessionStorage and retry —
                        // this falls through to Home or welcome screen
                        sessionStorage.removeItem('sb:active-workspace')
                        sessionStorage.removeItem('sb:guest-active')
                        sessionStorage.removeItem('sb:pending-workspace')
                        // Also clear any guest-cap entries
                        Object.keys(sessionStorage).filter(k => k.startsWith('sb:guest-cap:')).forEach(k => sessionStorage.removeItem(k))
                        // Clear hash to avoid retrying the same bad URL
                        history.replaceState(null, '', location.pathname + location.search)
                        setBootstrapError(null)
                        setRetryNonce(v => v + 1)
                    }}>Gå till startsidan</button>
                </div>
            </div>
        )
    }

    if (!workspace) {
        const progress = Math.min(95, 10 + loadingSeconds * 8)
        return (
            <div className="workspace-bootstrap-state">
                <div className="bootstrap-progress-track">
                    <div
                        className="bootstrap-progress-bar"
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <SkeletonFullPage />
            </div>
        )
    }

    return (
        <EngineManagerContext.Provider value={manager}>
            <WorkspaceContext.Provider value={workspace}>
                <SecureBlobProvider session={session}>
                    {children}
                </SecureBlobProvider>
            </WorkspaceContext.Provider>
        </EngineManagerContext.Provider>
    )
}

function useWorkspaceCtx(): Workspace {
    const ws = useContext(WorkspaceContext)
    if (!ws) throw new Error('useWorkspace must be used within WorkspaceProvider')
    return ws
}

// --- useEngineManager ---

export function useEngineManager(): EngineManager {
    const mgr = useContext(EngineManagerContext)
    if (!mgr) throw new Error('useEngineManager must be used within WorkspaceProvider')
    return mgr
}

// --- useRegistry: workspace entries from EngineManager ---

export interface UseRegistryResult {
    entries: WorkspaceEntry[]
    addEntry: (entry: WorkspaceEntry) => void
    removeEntry: (blobId: string) => void
    updateEntry: (blobId: string, updates: Partial<Pick<WorkspaceEntry, 'label' | 'emoji' | 'notifications'>>) => void
}

export function useRegistry(): UseRegistryResult {
    const mgr = useEngineManager()
    const [entries, setEntries] = useState<WorkspaceEntry[]>(() => mgr.getRegistry())

    useEffect(() => {
        return mgr.on('registry:changed', () => setEntries(mgr.getRegistry()))
    }, [mgr])

    const addEntry = useCallback((entry: WorkspaceEntry) => mgr.addToRegistry(entry), [mgr])
    const removeEntry = useCallback((blobId: string) => mgr.removeFromRegistry(blobId), [mgr])
    const updateEntry = useCallback((blobId: string, updates: Partial<Pick<WorkspaceEntry, 'label' | 'emoji' | 'notifications'>>) => mgr.updateRegistryEntry(blobId, updates), [mgr])

    return { entries, addEntry, removeEntry, updateEntry }
}

export interface RegistrySyncStatus {
    state: 'idle' | 'syncing' | 'synced' | 'error'
    isInitial: boolean
    error?: string
}

/** Tracks registry sync status — surfaces initial sync failures for UX (e.g. post-recovery error banner). */
export function useRegistrySyncStatus(): RegistrySyncStatus {
    const mgr = useEngineManager()
    const [status, setStatus] = useState<RegistrySyncStatus>({ state: 'idle', isInitial: true })

    useEffect(() => {
        return mgr.on('registry:sync-status', (payload) => {
            setStatus({ state: payload.state, isInitial: payload.isInitial, error: payload.error })
        })
    }, [mgr])

    return status
}

/**
 * Hook to create a brand-new workspace and activate it.
 * Returns null for guests (no identity).
 */
export function useCreateWorkspace(): (() => Promise<void>) | null {
    const mgr = useEngineManager()
    const hasId = mgr.hasIdentity()

    const create = useCallback(async () => {
        const blobId = crypto.randomUUID()
        const capability = generateWorkspaceSecretV2(blobId)
        const entry: WorkspaceEntry = {
            blobId,
            label: 'Untitled',
            emoji: '📝',
            apiBaseUrl: API_BASE_URL,
            capability,
            canWrite: true,
            trustLevel: 'trusted',
            agentSecretsAllowed: true,
        }
        mgr.addToRegistry(entry)
        const ws = await mgr.activateWorkspace(blobId)

        // §5.3.1: Clean genesis — empty commit establishes writer kid
        // without polluting workspace history with scaffold files.
        await ws.createGenesisCommit()
    }, [mgr])

    return hasId ? create : null
}

export interface WorkspaceSwitchState {
    activeBlobId: string | null
    suspendedBlobIds: string[]
    syncStates: Record<string, SyncState>
    activateWorkspace: (blobId: string) => Promise<void>
}

export function useWorkspaceSwitch(): WorkspaceSwitchState {
    const mgr = useEngineManager()
    const [activeBlobId, setActiveBlobId] = useState<string | null>(() => mgr.getActiveWorkspace()?.blobId ?? null)
    const [suspendedBlobIds, setSuspendedBlobIds] = useState<string[]>(() => mgr.getSuspendedWorkspaces().map(ws => ws.blobId))
    const [syncStates, setSyncStates] = useState<Record<string, SyncState>>({})

    useEffect(() => mgr.on('workspace:switched', ws => {
        setActiveBlobId(ws?.blobId ?? null)
        setSuspendedBlobIds(mgr.getSuspendedWorkspaces().map(item => item.blobId))
    }), [mgr])

    useEffect(() => mgr.on('workspace:sync-status', ({ blobId, state }) => {
        setSyncStates(prev => ({ ...prev, [blobId]: state }))
    }), [mgr])

    const activateWorkspace = useCallback(async (blobId: string) => {
        await mgr.activateWorkspace(blobId)
        setSuspendedBlobIds(mgr.getSuspendedWorkspaces().map(item => item.blobId))
    }, [mgr])

    return { activeBlobId, suspendedBlobIds, syncStates, activateWorkspace }
}

// --- useIdentity: identity from EngineManager ---

export interface UseIdentityResult {
    hasIdentity: boolean
    identity: UserIdentity | null
    createIdentity: (params?: { name?: string; emoji?: string; pin?: string; webAuthnOnly?: boolean }) => Promise<UserIdentity | null>
    importIdentity: (payload: string) => Promise<UserIdentity>
    exportIdentity: () => Promise<string>
    logoutIdentity: () => void
    deleteIdentity: () => Promise<void>
    unlockWithWebAuthn: () => Promise<boolean>
    vaultExists: boolean | null  // null = still checking
    vaultUnlockMethod: 'webauthn' | 'webauthn-prf' | 'pin' | null  // null = no vault or still checking
    updateProfile: (profile: { name?: string; emoji?: string }) => Promise<void>
    recoverFromServer: () => Promise<boolean>
    isGuest: boolean
    // Trusted Device management (§05 §10, §148)
    trustDevice: (label?: string) => Promise<{ deviceId: string }>
    getTrustedDevices: () => Promise<TrustedDeviceInfo[]>
    revokeTrustedDevice: (deviceId: string) => Promise<void>
    revokeAllTrustedDevices: () => Promise<void>
    isCurrentDeviceTrusted: () => Promise<boolean>
}

export function useIdentity(): UseIdentityResult {
    const mgr = useEngineManager()
    const [identity, setIdentity] = useState<UserIdentity | null>(() => mgr.getIdentity())
    const [vaultExists, setVaultExists] = useState<boolean | null>(null)
    const [vaultUnlockMethod, setVaultUnlockMethod] = useState<'webauthn' | 'webauthn-prf' | 'pin' | null>(null)

    const refreshVaultState = useCallback(() => {
        mgr.identity.hasVaultRecord().then(exists => setVaultExists(exists)).catch(() => setVaultExists(false))
        mgr.identity.getVaultUnlockMethod().then(m => setVaultUnlockMethod(m)).catch(() => setVaultUnlockMethod(null))
    }, [mgr])

    useEffect(() => {
        refreshVaultState()
        const unsub = mgr.on('identity:changed', () => {
            setIdentity(mgr.getIdentity())
            refreshVaultState()
        })
        return () => {
            unsub()
        }
    }, [mgr, refreshVaultState])

    const createIdentity = useCallback((params?: { name?: string; emoji?: string; pin?: string; webAuthnOnly?: boolean }) =>
        mgr.createIdentity(params), [mgr])
    const importIdentity = useCallback((payload: string) => mgr.importIdentity(payload), [mgr])
    const exportIdentity = useCallback(() => mgr.exportIdentity(), [mgr])
    const logoutIdentity = useCallback(() => mgr.logoutIdentity(), [mgr])
    const deleteIdentity = useCallback(() => mgr.deleteIdentity(), [mgr])
    const unlockWithWebAuthn = useCallback(() => mgr.unlockWithWebAuthn(), [mgr])
    const updateProfile = useCallback((profile: { name?: string; emoji?: string }) => mgr.updateProfile(profile), [mgr])
    const recoverFromServer = useCallback(() => mgr.recoverFromServer(), [mgr])
    const trustDevice = useCallback((label?: string) => mgr.trustDevice(label), [mgr])
    const getTrustedDevices = useCallback(() => mgr.getTrustedDevices(), [mgr])
    const revokeTrustedDevice = useCallback((deviceId: string) => mgr.revokeTrustedDevice(deviceId), [mgr])
    const revokeAllTrustedDevices = useCallback(() => mgr.revokeAllTrustedDevices(), [mgr])
    const isCurrentDeviceTrusted = useCallback(() => mgr.isCurrentDeviceTrusted(), [mgr])


    return {
        hasIdentity: identity !== null,
        identity,
        createIdentity,
        importIdentity,
        exportIdentity,
        logoutIdentity,
        deleteIdentity,
        unlockWithWebAuthn,
        vaultExists,
        vaultUnlockMethod,
        updateProfile,
        recoverFromServer,
        isGuest: identity === null,
        trustDevice,
        getTrustedDevices,
        revokeTrustedDevice,
        revokeAllTrustedDevices,
        isCurrentDeviceTrusted,
    }
}

// --- useReadFile: async readText for on-demand content reads ---

export function useReadFile(): (path: string) => Promise<string | null> {
    const ws = useWorkspaceCtx()
    return useCallback((path: string) => ws.fs.readText(path), [ws])
}

// --- useSync: syncing state, lastSyncAt, pendingChanges ---

export interface UseSyncResult {
    syncing: boolean
    lastSyncAt: number
    pendingChanges: number
    lastSyncError?: SyncState['lastSyncError']
    connectivity: 'connected' | 'offline' | 'unreachable'
    online: boolean
    synced: boolean
    syncLog: SyncState['syncLog']
    conflictPaths?: string[]
    syncProgress?: SyncState['syncProgress']
    syncPolicy: WorkspaceSyncPolicy
    syncPaused?: boolean
    syncNow: () => Promise<void>
    setSyncPolicy: (policy: WorkspaceSyncPolicy) => void
    pauseSync: () => void
    resumeSync: () => void
}

function snapshotSyncState(state: SyncState) {
    return {
        syncing: state.syncing,
        lastSyncAt: state.lastSyncAt,
        pendingChanges: state.pendingChanges,
        lastSyncError: state.lastSyncError,
        online: state.online,
        connectivity: state.connectivity,
        synced: state.synced,
        syncLog: state.syncLog,
        conflictPaths: state.conflictPaths,
        syncProgress: state.syncProgress,
        syncPolicy: state.syncPolicy ?? (state.syncPaused ? 'paused' : 'on-demand'),
        syncPaused: state.syncPaused,
    }
}

export function useSync(): UseSyncResult {
    const ws = useWorkspaceCtx()
    const [syncState, setSyncState] = useState(() => snapshotSyncState(ws.state))

    useEffect(() => {
        setSyncState(snapshotSyncState(ws.state))
        return ws.on('state:changed', (s: SyncState) => {
            setSyncState(prev => ({
                syncing: s.syncing,
                lastSyncAt: s.lastSyncAt ?? prev.lastSyncAt,
                pendingChanges: s.pendingChanges ?? 0,
                lastSyncError: s.lastSyncError,
                online: s.online,
                connectivity: s.connectivity,
                synced: s.synced,
                syncLog: s.syncLog,
                conflictPaths: s.conflictPaths,
                syncProgress: s.syncProgress,
                syncPolicy: s.syncPolicy ?? (s.syncPaused ? 'paused' : 'on-demand'),
                syncPaused: s.syncPaused,
            }))
        })
    }, [ws])

    // §7.6.1 Unified manual sync — syncs workspace + registry + MCP config-blobs in parallel
    const mgr = useEngineManager()
    const syncNow = useCallback(() => mgr.syncAllNow(), [mgr])
    const setSyncPolicy = useCallback((policy: WorkspaceSyncPolicy) => ws.setSyncPolicy(policy), [ws])
    const pauseSync = useCallback(() => ws.pauseSync(), [ws])
    const resumeSync = useCallback(() => ws.resumeSync(), [ws])

    return { ...syncState, syncNow, setSyncPolicy, pauseSync, resumeSync }
}

// --- useFileSystem: file listing, watching for changes ---

export interface UseFileSystemResult {
    files: FileStat[]
    loadingDirs: ReadonlySet<string>
    loadedDirs: ReadonlySet<string>
    dirErrors: Readonly<Record<string, string>>
    isLoadingRoot: boolean
    loadDir: (path: string, opts?: { force?: boolean }) => Promise<void>
    reloadDir: (path: string) => Promise<void>
    createFile: (path: string, content?: string) => void
    writeFile: (path: string, content?: string) => void
    deleteFile: (path: string) => void
    renameFile: (oldPath: string, newPath: string) => void
    readFile: (path: string) => Promise<string | null>
    mkdir: (path: string) => void
}

function normalizeFsDirPath(path: string): string {
    if (!path || path === '/') return '/'
    const normalized = path.replace(/\\/g, '/').replace(/\/+$/, '')
    return normalized.startsWith('/') ? normalized : `/${normalized}`
}

function parentFsDirPath(path: string): string {
    const normalized = normalizeFsDirPath(path)
    const idx = normalized.lastIndexOf('/')
    return idx <= 0 ? '/' : normalized.slice(0, idx)
}

function normalizeFsEntryPath(entry: FileStat): string {
    if (entry.kind !== 'directory') return entry.path
    return normalizeFsDirPath(entry.path)
}

function mergeFileStats(...groups: FileStat[][]): FileStat[] {
    const byPath = new Map<string, FileStat>()
    for (const group of groups) {
        for (const entry of group) {
            const path = normalizeFsEntryPath(entry)
            byPath.set(path, { ...entry, path })
        }
    }
    return [...byPath.values()].sort((a, b) => a.path.localeCompare(b.path))
}

export function useFileSystem(): UseFileSystemResult {
    const ws = useWorkspaceCtx()
    const [localFiles, setLocalFiles] = useState<FileStat[]>(() => ws.fs.list())
    const [entriesByDir, setEntriesByDir] = useState<Record<string, FileStat[]>>({})
    const [loadingDirs, setLoadingDirs] = useState<Set<string>>(new Set())
    const [loadedDirs, setLoadedDirs] = useState<Set<string>>(new Set())
    const [dirErrors, setDirErrors] = useState<Record<string, string>>({})
    const loadingDirsRef = useRef<Set<string>>(new Set())
    const loadedDirsRef = useRef<Set<string>>(new Set())
    const refreshSeqRef = useRef(0)
    const dirRequestSeqRef = useRef<Record<string, number>>({})

    const refreshLocalSnapshot = useCallback(() => {
        setLocalFiles(ws.fs.list())
    }, [ws])

    const loadDir = useCallback(async (rawPath: string, opts?: { force?: boolean }) => {
        const path = normalizeFsDirPath(rawPath)
        if (!opts?.force && (loadedDirsRef.current.has(path) || loadingDirsRef.current.has(path))) return

        const seq = refreshSeqRef.current
        const requestSeq = (dirRequestSeqRef.current[path] ?? 0) + 1
        dirRequestSeqRef.current[path] = requestSeq
        const isCurrentRequest = () => (
            seq === refreshSeqRef.current && dirRequestSeqRef.current[path] === requestSeq
        )
        loadingDirsRef.current.add(path)
        setLoadingDirs(prev => new Set(prev).add(path))
        setDirErrors(prev => {
            const next = { ...prev }
            delete next[path]
            return next
        })

        try {
            const entries = await ws.fs.listDirAsync(path)
            if (!isCurrentRequest()) return
            const normalizedEntries = entries.map(entry => ({
                ...entry,
                path: normalizeFsEntryPath(entry),
            }))
            loadedDirsRef.current.add(path)
            setLoadedDirs(prev => new Set(prev).add(path))
            setEntriesByDir(prev => ({ ...prev, [path]: normalizedEntries }))
            setLocalFiles(ws.fs.list())
        } catch (error) {
            if (!isCurrentRequest()) return
            setDirErrors(prev => ({
                ...prev,
                [path]: error instanceof Error ? error.message : String(error),
            }))
        } finally {
            if (isCurrentRequest()) {
                loadingDirsRef.current.delete(path)
                setLoadingDirs(prev => {
                    const next = new Set(prev)
                    next.delete(path)
                    return next
                })
            }
        }
    }, [ws])

    const reloadDir = useCallback((path: string) => loadDir(path, { force: true }), [loadDir])

    const reloadLoadedDirs = useCallback(() => {
        refreshLocalSnapshot()
        for (const dir of loadedDirsRef.current) {
            void loadDir(dir, { force: true })
        }
    }, [loadDir, refreshLocalSnapshot])

    useEffect(() => {
        refreshSeqRef.current += 1
        dirRequestSeqRef.current = {}
        loadingDirsRef.current.clear()
        loadedDirsRef.current.clear()
        setEntriesByDir({})
        setLoadingDirs(new Set())
        setLoadedDirs(new Set())
        setDirErrors({})
        refreshLocalSnapshot()
        void loadDir('/', { force: true })

        const reloadTouchedParents = (ev?: FsChangeEvent | FsExternalChangeEvent) => {
            refreshLocalSnapshot()
            if ((ev?.paths ?? []).some(path => normalizeFsDirPath(path) === '/')) {
                reloadLoadedDirs()
                return
            }
            const parents = new Set<string>()
            for (const path of ev?.paths ?? []) {
                parents.add(path === '/' ? '/' : parentFsDirPath(path))
                if (path.endsWith('/')) parents.add(normalizeFsDirPath(path))
            }
            if (parents.size === 0) {
                reloadLoadedDirs()
                return
            }
            for (const parent of parents) {
                if (loadedDirsRef.current.has(parent)) {
                    void loadDir(parent, { force: true })
                }
            }
        }

        const unsub1 = ws.on('fs:changed', reloadTouchedParents)
        const unsub2 = ws.on('fs:external-change', reloadTouchedParents)
        const unsub3 = ws.on('state:changed', refreshLocalSnapshot)
        return () => { unsub1(); unsub2(); unsub3() }
    }, [ws, loadDir, reloadLoadedDirs, refreshLocalSnapshot])

    const files = useMemo(() => mergeFileStats(
        ...Object.values(entriesByDir),
        localFiles,
    ), [entriesByDir, localFiles])

    const createFile = useCallback((path: string, content?: string) => {
        const initialContent = content ?? (path.endsWith('.agent.json') ? DEFAULT_AGENT_CONFIG_JSON : '')
        ws.fs.writeText(path, initialContent)
    }, [ws])

    const deleteFile = useCallback((path: string) => {
        ws.fs.delete(path)
    }, [ws])

    const renameFile = useCallback((oldPath: string, newPath: string) => {
        ws.fs.rename(oldPath, newPath)
    }, [ws])

    const readFile = useCallback((path: string) => ws.fs.readText(path), [ws])

    const mkdir = useCallback((path: string) => {
        ws.fs.mkdir(path)
    }, [ws])

    return {
        files,
        loadingDirs,
        loadedDirs,
        dirErrors,
        isLoadingRoot: loadingDirs.has('/') && !loadedDirs.has('/'),
        loadDir,
        reloadDir,
        createFile,
        writeFile: createFile,
        deleteFile,
        renameFile,
        readFile,
        mkdir,
    }
}

// --- useEditor: read/write a single file + auto-apply external changes ---

export interface UseEditorResult {
    content: string | null
    stat: FileStat | null
    updateContent: (newContent: string) => void
    saveVersion: number
    /** True while the file is expected to arrive via sync (stat was null on mount).
     *  Consumers should show a loading skeleton instead of "File not found" while true. */
    awaitingSync: boolean
}

export function useEditor(path: string | null): UseEditorResult {
    const ws = useWorkspaceCtx()
    const [content, setContent] = useState<string | null>(null)
    const [stat, setStat] = useState<FileStat | null>(null)
    const [loadedPath, setLoadedPath] = useState<string | null>(path)
    const [saveVersion, setSaveVersion] = useState(0)
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const localContentRef = useRef<string | null>(null)
    const writeEpochRef = useRef(0)
    const readRequestRef = useRef(0)
    // Track the pending debounced write so we can flush it on path change
    // without losing user edits.  { path, content } mirrors what the timer
    // closure would write — stored here so the load effect can replay it
    // synchronously before switching to a new file.
    const pendingWriteRef = useRef<{ path: string; content: string } | null>(null)
    // awaitingSync: true while stat is null AND initial sync hasn't delivered data yet.
    // Prevents "File not found" flash during share-link entry (file arrives via sync).
    const [awaitingSync, setAwaitingSync] = useState(false)

    // ── Flush helper ─────────────────────────────────────────────
    // Cancels the debounce timer and synchronously persists the
    // pending edit to its ORIGINAL path.  Called on path switch,
    // on deselect (path→null), and on unmount — so user edits are
    // never silently dropped.
    const flushPendingSave = useCallback(() => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current)
            saveTimerRef.current = null
        }
        if (pendingWriteRef.current) {
            ws.fs.writeText(pendingWriteRef.current.path, pendingWriteRef.current.content)
            pendingWriteRef.current = null
        }
    }, [ws])

    // Load file content
    useEffect(() => {
        // Flush pending save from the previous file BEFORE anything
        // else — this covers both path→newPath and path→null cases.
        flushPendingSave()
        localContentRef.current = null
        writeEpochRef.current = 0
        readRequestRef.current++

        if (!path) {
            setContent(null)
            setStat(null)
            setLoadedPath(null)
            setAwaitingSync(false)
            return
        }

        let cancelled = false
        setContent(null)
        const initialStat = ws.fs.stat(path)
        setStat(initialStat)
        setLoadedPath(path)
        // If file doesn't exist locally yet, it may arrive via sync
        setAwaitingSync(!initialStat)

        const epochAtCall = writeEpochRef.current
        const requestId = ++readRequestRef.current
        ws.fs.readText(path).then(text => {
            if (cancelled) return
            if (readRequestRef.current !== requestId) return
            if (writeEpochRef.current !== epochAtCall) return
            setContent(text)
            localContentRef.current = text
            setLoadedPath(path)
            // If readText returned content, file exists — no longer awaiting sync
            if (text !== null) {
                setStat(ws.fs.stat(path))
                setAwaitingSync(false)
            }
        })

        return () => { cancelled = true }
    }, [ws, path])

    // Clear awaitingSync when any fs:external-change fires (initial sync completed)
    // or after a 10s timeout (sync failed / file truly doesn't exist).
    useEffect(() => {
        if (!awaitingSync || !path) return
        let cleared = false
        const clear = () => {
            if (cleared) return
            cleared = true
            setAwaitingSync(false)
        }
        const unsub = ws.on('fs:external-change', clear)
        const timer = setTimeout(clear, 10_000)
        return () => { unsub(); clearTimeout(timer) }
    }, [ws, path, awaitingSync])

    // Auto-apply external changes.
    // Only listen to fs:external-change — every sync path that modifies files
    // MUST emit this event (spec §3.2.2). state:changed is for sync metadata
    // only and MUST NOT be used for content updates (causes feedback loops).
    useEffect(() => {
        if (!path) return

        const applyExternalContent = () => {
            // If an autosave debounce is pending, skip — the save will commit
            // and the next sync cycle will bring back the merged result
            if (saveTimerRef.current) return

            // Capture epoch before async gap — if user types during readText(),
            // the epoch will change and we'll discard the stale result
            const epochAtCall = writeEpochRef.current
            const requestId = ++readRequestRef.current

            ws.fs.readText(path).then(remote => {
                if (remote === null) return
                if (readRequestRef.current !== requestId) return
                // User typed during the async gap → readText result is stale
                if (writeEpochRef.current !== epochAtCall) return
                if (remote !== localContentRef.current) {
                    setContent(remote)
                    localContentRef.current = remote
                    setStat(ws.fs.stat(path))
                    setLoadedPath(path)
                    setAwaitingSync(false)
                }
            })
        }

        const unsubExternal = ws.on('fs:external-change', (ev: FsExternalChangeEvent) => {
            if (ev.paths.includes(path)) applyExternalContent()
        })

        return () => { unsubExternal() }
    }, [ws, path])

    // Also react to fs:changed — embedded agent writes go through
    // workspace.fs.writeText() which emits fs:changed (not fs:external-change).
    // The saveTimerRef guard prevents feedback loops: when the EDITOR saves,
    // saveTimerRef is set (debounce pending); when the AGENT writes externally,
    // no save timer is pending so content is re-read and applied.
    useEffect(() => {
        if (!path) return
        const onLocalChange = (ev: FsChangeEvent) => {
            if (!ev.paths.includes(path)) return
            // Skip if WE have a pending autosave — we are the source
            if (saveTimerRef.current) return

            const epochAtCall = writeEpochRef.current
            const requestId = ++readRequestRef.current
            ws.fs.readText(path).then(remote => {
                if (remote === null) return
                if (readRequestRef.current !== requestId) return
                if (writeEpochRef.current !== epochAtCall) return
                if (remote !== localContentRef.current) {
                    setContent(remote)
                    localContentRef.current = remote
                    setStat(ws.fs.stat(path))
                    setLoadedPath(path)
                }
            })
        }
        return ws.on('fs:changed', onLocalChange)
    }, [ws, path])

    const updateContent = useCallback((newContent: string) => {
        // Skip no-op writes: when external sync updates content via
        // applyExternalContent → setContent → CodeMirror prop change →
        // CM6 onChange, the newContent matches what we just set.
        // Without this guard, the debounced writeText creates an
        // unnecessary commit that echoes the remote change back.
        if (newContent === localContentRef.current) return

        setContent(newContent)
        localContentRef.current = newContent
        writeEpochRef.current++
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
        pendingWriteRef.current = path ? { path, content: newContent } : null
        saveTimerRef.current = setTimeout(() => {
            if (path) {
                ws.fs.writeText(path, newContent)
                setSaveVersion(v => v + 1)
            }
            pendingWriteRef.current = null
            // Clear AFTER writeText so the fs:changed handler (which checks
            // saveTimerRef) correctly skips the editor's own save event.
            saveTimerRef.current = null
        }, 500)
    }, [ws, path])

    useEffect(() => () => {
        // Flush on unmount — don't lose edits when the editor closes
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current)
            saveTimerRef.current = null
        }
        if (pendingWriteRef.current) {
            ws.fs.writeText(pendingWriteRef.current.path, pendingWriteRef.current.content)
            pendingWriteRef.current = null
        }
    }, [])

    const liveStat = path ? ws.fs.stat(path) : null
    const visibleContent = loadedPath === path ? content : null
    const visibleStat = loadedPath === path ? stat : liveStat
    const visibleAwaitingSync = loadedPath === path ? awaitingSync : !!path && !liveStat

    return {
        content: visibleContent,
        stat: visibleStat,
        updateContent,
        saveVersion,
        awaitingSync: visibleAwaitingSync,
    }
}

export interface UseHistoryResult {
    commits: CommitSummary[]
    listForFile: (path: string) => CommitSummary[]
    getCommit: (hash: string) => Promise<CommitSummary>
    blame: (path: string) => Promise<import('../engine/types').BlameLine[]>
    readTextAtCommit: (path: string, commitHash: string) => Promise<string | null>
    diffText: (path: string, fromCommit: string, toCommit: string) => Promise<import('../engine/types').DiffHunk[]>
}

export function useHistory(): UseHistoryResult {
    const ws = useWorkspaceCtx()
    const [commits, setCommits] = useState<CommitSummary[]>(() => ws.history.list())

    useEffect(() => {
        const refresh = () => setCommits(ws.history.list())
        const unsub = ws.on('history:changed', refresh)
        const unsub2 = ws.on('fs:changed', refresh)
        return () => { unsub(); unsub2() }
    }, [ws])

    const listForFile = useCallback((path: string) => ws.history.listForFile(path), [ws])
    const getCommit = useCallback((hash: string) => ws.history.getCommit(hash), [ws])
    const blame = useCallback((path: string) => ws.history.blame(path), [ws])
    const readTextAtCommit = useCallback((path: string, hash: string) => ws.history.readTextAtCommit(path, hash), [ws])
    const diffText = useCallback((path: string, from: string, to: string) => ws.history.diffText(path, from, to), [ws])

    return { commits, listForFile, getCommit, blame, readTextAtCommit, diffText }
}

export interface UseSharingResult {
    createShareLink: (mode: 'read' | 'readwrite', path?: string, viewMode?: 'edit' | 'preview') => import('../engine/types').ShareLink
    createInviteLink: (mode: 'read' | 'readwrite', path?: string, viewMode?: 'edit' | 'preview') => Promise<import('../engine/types').ShareLink>
}

export function useSharing(): UseSharingResult {
    const ws = useWorkspaceCtx()
    const createShareLink = useCallback(
        (mode: 'read' | 'readwrite', path?: string, viewMode?: 'edit' | 'preview') => ws.sharing.createShareLink(mode, path, viewMode),
        [ws],
    )
    const createInviteLink = useCallback(
        (mode: 'read' | 'readwrite', path?: string, viewMode?: 'edit' | 'preview') => ws.sharing.createInviteLink(mode, path, viewMode),
        [ws],
    )
    return { createShareLink, createInviteLink }
}

export interface UseWorkspaceMetadataResult {
    blobId: string
    canWrite: boolean
    label: string
    emoji: string
}

export function useWorkspaceMetadata(): UseWorkspaceMetadataResult {
    const ws = useWorkspaceCtx()
    const { entries } = useRegistry()
    const registryEntry = entries.find(e => e.blobId === ws.blobId)
    return useMemo(() => ({
        blobId: ws.blobId,
        canWrite: ws.canWrite,
        label: registryEntry?.label ?? ws.label,
        emoji: registryEntry?.emoji ?? ws.emoji,
    }), [ws, registryEntry?.label, registryEntry?.emoji])
}

export { useWorkspaceCtx as useWorkspace }
export type { Workspace } from '../engine/types'
